import { useCallback, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Col,
  Form,
  Modal,
  Nav,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  approveEarlyStageUser,
  cancelEarlyStageAdminDecision,
  getEarlyStageAdminApprovals,
  rejectEarlyStageUser,
  type EarlyStageAdminApproval,
} from "../../api/earlyStageMembershipApi";
import { FiCheckSquare } from "react-icons/fi";
import AdminPanelHeader from "./AdminPanelHeader";

type AdminTab = "under23Requests" | "awaitingPayment" | "activeMembers";

function isActivePaidEarlyStageMember(row: EarlyStageAdminApproval): boolean {
  return (
    row.hasPaid === 1 &&
    !!row.customerId?.trim() &&
    !!row.subscriptionId?.trim() &&
    row.stripeStatus?.trim().toLowerCase() === "active"
  );
}

function isUnder23RequestRow(row: EarlyStageAdminApproval): boolean {
  return row.isActiveQueue;
}

function isProceedingAtPremium10WhilePending(
  row: EarlyStageAdminApproval
): boolean {
  return (
    row.isActiveQueue &&
    row.priceTier === "Premium10"
  );
}

function isAwaitingPaymentRow(row: EarlyStageAdminApproval): boolean {
  if (isActivePaidEarlyStageMember(row)) return false;
  if (row.isActiveQueue) return false;

  const status = row.verificationStatus;
  const directPremium10 =
    row.ageTier === "TwentyThreePlus" ||
    row.priceTier === "Premium10" ||
    status === "NotSubmitted" ||
    row.displayStatus === "Not Applied";
  return (
    status === "Approved" ||
    status === "Rejected" ||
    status === "Expired" ||
    directPremium10
  );
}

function canCancelAdminDecision(row: EarlyStageAdminApproval): boolean {
  if (row.ageTier !== "Under23") return false;
  const status = row.verificationStatus;
  if (status === "Approved" && row.verifiedAt) return true;
  if (status === "Rejected" && row.rejectedAt && row.rejectedReason?.trim())
    return true;
  return false;
}

function statusBadgeVariant(
  displayStatus: string
): "warning" | "success" | "danger" | "secondary" | "info" | "primary" {
  switch (displayStatus) {
    case "Pending Review":
    case "Under Review":
      return "warning";
    case "Approved":
      return "success";
    case "Rejected":
      return "danger";
    case "Expired":
    case "Not Applied":
      return "secondary";
    default:
      return "info";
  }
}

function formatAgeTier(ageTier?: string | null): string {
  if (ageTier === "Under23") return "Young Professional";
  if (ageTier === "TwentyThreePlus") return "23+";
  return ageTier?.trim() || "—";
}

const PENDING_APPLICATION_DETAIL =
  "Young Professional application — ID on file, awaiting review";

const MODAL_BTN_BASE =
  "d-inline-flex align-items-center justify-content-center text-center rounded-2 fw-medium shadow-sm px-4 py-2";
const MODAL_BTN_SECONDARY = `${MODAL_BTN_BASE} text-dark border-0`;
const MODAL_BTN_PRIMARY = `${MODAL_BTN_BASE} text-white border-0 bg-project-gradient`;
const MODAL_BTN_DANGER = `${MODAL_BTN_BASE} border`;

const EarlyStageVerifications = () => {
  const queryClient = useQueryClient();
  const [adminTab, setAdminTab] = useState<AdminTab>("under23Requests");
  const [detailRow, setDetailRow] = useState<EarlyStageAdminApproval | null>(
    null
  );
  const [rejectTarget, setRejectTarget] =
    useState<EarlyStageAdminApproval | null>(null);
  const [approveTarget, setApproveTarget] =
    useState<EarlyStageAdminApproval | null>(null);
  const [cancelTarget, setCancelTarget] =
    useState<EarlyStageAdminApproval | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [actingId, setActingId] = useState<number | null>(null);

  const { data: approvals = [], isLoading, refetch } = useQuery({
    queryKey: ["early-stage-admin-approvals"],
    queryFn: getEarlyStageAdminApprovals,
  });

  const under23RequestRows = useMemo(
    () => approvals.filter(isUnder23RequestRow),
    [approvals]
  );
  const awaitingPaymentRows = useMemo(
    () => approvals.filter(isAwaitingPaymentRow),
    [approvals]
  );
  const activeMemberRows = useMemo(
    () => approvals.filter(isActivePaidEarlyStageMember),
    [approvals]
  );
  const visibleRows =
    adminTab === "under23Requests"
      ? under23RequestRows
      : adminTab === "awaitingPayment"
        ? awaitingPaymentRows
        : activeMemberRows;
  const isUnder23Tab = adminTab === "under23Requests";
  const isAwaitingPaymentTab = adminTab === "awaitingPayment";
  const isActiveMembersTab = adminTab === "activeMembers";

  const invalidate = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: ["early-stage-admin-approvals"],
    });
    await queryClient.invalidateQueries({ queryKey: ["early-stage-pending"] });
    refetch();
  }, [queryClient, refetch]);

  const onApproveConfirm = useCallback(async () => {
    if (!approveTarget) return;
    const fromUnder23Tab = approveTarget.isActiveQueue;
    setActingId(approveTarget.userId);
    try {
      await approveEarlyStageUser(approveTarget.userId);
      toast.success("Approved.");
      setApproveTarget(null);
      await invalidate();
      if (fromUnder23Tab) setAdminTab("awaitingPayment");
    } catch {
      toast.error("Could not approve.");
    } finally {
      setActingId(null);
    }
  }, [approveTarget, invalidate]);

  const onRejectConfirm = useCallback(async () => {
    if (!rejectTarget || !rejectReason.trim()) {
      toast.error("Please enter a rejection reason.");
      return;
    }
    const fromUnder23Tab = rejectTarget.isActiveQueue;
    setActingId(rejectTarget.userId);
    try {
      await rejectEarlyStageUser(rejectTarget.userId, rejectReason.trim());
      toast.success("Rejected.");
      setRejectTarget(null);
      setRejectReason("");
      await invalidate();
      if (fromUnder23Tab) setAdminTab("awaitingPayment");
    } catch {
      toast.error("Could not reject.");
    } finally {
      setActingId(null);
    }
  }, [invalidate, rejectReason, rejectTarget]);

  const onCancelConfirm = useCallback(async () => {
    if (!cancelTarget) return;
    setActingId(cancelTarget.userId);
    try {
      await cancelEarlyStageAdminDecision(cancelTarget.userId);
      toast.success("Decision cancelled — application returned to pending review.");
      setCancelTarget(null);
      await invalidate();
      setAdminTab("under23Requests");
    } catch {
      toast.error("Could not cancel decision.");
    } finally {
      setActingId(null);
    }
  }, [cancelTarget, invalidate]);

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="w-100">
      <AdminPanelHeader
        icon={<FiCheckSquare size={22} />}
        title="Early-Stage Approvals"
        description="Approve or reject early-stage membership applications."
      />

      <Nav variant="tabs" className="mb-3">
        <Nav.Item>
          <Nav.Link
            active={adminTab === "under23Requests"}
            onClick={() => setAdminTab("under23Requests")}
            className={
              adminTab === "under23Requests"
                ? "text-primary fw-semibold"
                : "text-dark"
            }
          >
            Pending Applications ({under23RequestRows.length})
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            active={adminTab === "awaitingPayment"}
            onClick={() => setAdminTab("awaitingPayment")}
            className={
              adminTab === "awaitingPayment"
                ? "text-primary fw-semibold"
                : "text-dark"
            }
          >
            Payment Pending ({awaitingPaymentRows.length})
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            active={adminTab === "activeMembers"}
            onClick={() => setAdminTab("activeMembers")}
            className={
              adminTab === "activeMembers"
                ? "text-primary fw-semibold"
                : "text-dark"
            }
          >
            Active Members ({activeMemberRows.length})
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {visibleRows.length === 0 ? (
        <p className="text-muted">
          {isUnder23Tab
            ? "No applications pending review."
            : isAwaitingPaymentTab
              ? "No members with payment pending."
              : "No active memberships yet."}
        </p>
      ) : (
        <Table responsive bordered hover size="sm" className="mb-0 align-middle">
          <thead className="table-light">
            <tr>
              <th>Member</th>
              <th style={{ minWidth: 280 }}>Status</th>
              {isActiveMembersTab && <th>Subscription</th>}
              <th>Submitted</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => {
              const proceededAtTen =
                isUnder23Tab && isProceedingAtPremium10WhilePending(row);
              const displayStatusMeaning = row.statusMeaning
                ? row.statusMeaning.replace(/Young Entrepreneur/g, "Young Professional")
                : row.statusMeaning;

              return (
                <tr
                  key={row.userId}
                  onClick={() => setDetailRow(row)}
                  style={{ cursor: "pointer" }}
                >
                  <td>
                    <div className="fw-semibold">
                      {row.firstName} {row.lastName}
                    </div>
                    <div className="small text-muted">{row.email}</div>
                  </td>
                  <td>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <Badge bg={statusBadgeVariant(row.displayStatus)}>
                        {row.displayStatus}
                      </Badge>
                      {proceededAtTen && (
                        <span className="small text-success fw-medium">
                          Member already uses standard rate
                        </span>
                      )}
                    </div>
                    <div className="small text-muted mt-1">
                      {isUnder23Tab && row.isActiveQueue
                        ? PENDING_APPLICATION_DETAIL
                        : displayStatusMeaning}
                    </div>
                  </td>
                  {isActiveMembersTab && (
                    <td className="small">
                      <Badge bg="success">Active</Badge>
                      <div className="text-muted mt-1">
                        {row.priceTier === "Standard2"
                          ? "£2/month"
                          : row.priceTier === "Premium10"
                            ? "£10/month"
                            : row.priceTier || "—"}
                      </div>
                      <div
                        className="text-muted text-truncate"
                        style={{ maxWidth: 180 }}
                      >
                        {row.subscriptionId}
                      </div>
                    </td>
                  )}
                  <td className="small text-nowrap">
                    {row.submittedAt
                      ? new Date(row.submittedAt).toLocaleString()
                      : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      <Modal
        show={!!detailRow}
        onHide={() => setDetailRow(null)}
        size="lg"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {detailRow?.firstName} {detailRow?.lastName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "70vh" }}>
          {detailRow && (
            <>
              <Row className="g-3 mb-3">
                <Col md={6}>
                  <p className="mb-1">
                    <strong>Email:</strong> {detailRow.email}
                  </p>
                  <p className="mb-1">
                    <strong>Phone:</strong> {detailRow.phoneNumber || "—"}
                  </p>
                  <p className="mb-1">
                    <strong>Location:</strong>{" "}
                    {[detailRow.city, detailRow.country]
                      .filter(Boolean)
                      .join(", ") || "—"}
                  </p>
                  <p className="mb-1">
                    <strong>Age band:</strong> {formatAgeTier(detailRow.ageTier)}
                  </p>
                  <p className="mb-1">
                    <strong>Date of birth:</strong>{" "}
                    {detailRow.dateOfBirth || "—"}
                  </p>
                  <p className="mb-1">
                    <strong>Current status:</strong> {detailRow.displayStatus}
                  </p>
                  <p className="small text-muted mb-1">
                    {detailRow.statusMeaning}
                  </p>
                  <p className="mb-1">
                    <strong>Price tier:</strong> {detailRow.priceTier || "—"}
                  </p>
                  <p className="mb-0">
                    <strong>Paid:</strong>{" "}
                    {detailRow.hasPaid === 1 ? "Yes" : "No"}
                  </p>
                  {detailRow.customerId && (
                    <p className="mb-0 mt-1">
                      <strong>Stripe customer:</strong> {detailRow.customerId}
                    </p>
                  )}
                  {detailRow.subscriptionId && (
                    <p className="mb-0">
                      <strong>Subscription:</strong> {detailRow.subscriptionId}
                    </p>
                  )}
                  {detailRow.stripeStatus && (
                    <p className="mb-0">
                      <strong>Stripe status:</strong> {detailRow.stripeStatus}
                    </p>
                  )}
                </Col>
                <Col md={6}>
                  <p className="mb-2 fw-semibold">Submitted assets</p>
                  {detailRow.professionalPhotoSasUrl ? (
                    <div className="mb-3">
                      <p className="small text-muted mb-1">Profile photo</p>
                      <img
                        src={detailRow.professionalPhotoSasUrl}
                        alt="Profile"
                        className="img-fluid rounded border"
                        style={{ maxHeight: 160, objectFit: "contain" }}
                      />
                    </div>
                  ) : (
                    <p className="small text-muted">No profile photo on file.</p>
                  )}
                  {detailRow.idDocumentSasUrl ? (
                    <div>
                      <p className="small text-muted mb-1">ID document</p>
                      {detailRow.idDocumentSasUrl
                        .toLowerCase()
                        .includes(".pdf") ? (
                        <a
                          href={detailRow.idDocumentSasUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-primary"
                        >
                          Open ID (PDF)
                        </a>
                      ) : (
                        <img
                          src={detailRow.idDocumentSasUrl}
                          alt="ID document"
                          className="img-fluid rounded border"
                          style={{ maxHeight: 220, objectFit: "contain" }}
                        />
                      )}
                      <div className="mt-2">
                        <a
                          href={detailRow.idDocumentSasUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open full size
                        </a>
                      </div>
                    </div>
                  ) : (
                    <p className="small text-muted">No ID document uploaded.</p>
                  )}
                </Col>
              </Row>
              <hr />
              <p className="small text-muted mb-2">
                <strong>Submitted:</strong>{" "}
                {detailRow.submittedAt
                  ? new Date(detailRow.submittedAt).toLocaleString()
                  : "—"}
                {" · "}
                <strong>Verified:</strong>{" "}
                {detailRow.verifiedAt
                  ? new Date(detailRow.verifiedAt).toLocaleString()
                  : "—"}
                {" · "}
                <strong>Resubmissions:</strong> {detailRow.resubmissionCount}
              </p>
              {detailRow.rejectedReason && (
                <p className="small text-danger mb-0">
                  <strong>Last rejection reason:</strong>{" "}
                  {detailRow.rejectedReason}
                </p>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex flex-wrap gap-2 justify-content-end border-0 pt-0">
          <Button
            variant="light"
            className={MODAL_BTN_SECONDARY}
            onClick={() => setDetailRow(null)}
          >
            Close
          </Button>
          {detailRow && isUnder23Tab && detailRow.isActiveQueue && (
            <>
              <Button
                variant="outline-danger"
                className={MODAL_BTN_DANGER}
                disabled={actingId === detailRow.userId}
                onClick={() => {
                  setRejectTarget(detailRow);
                  setRejectReason(detailRow.rejectedReason ?? "");
                  setDetailRow(null);
                }}
              >
                Reject
              </Button>
              <Button
                className={MODAL_BTN_PRIMARY}
                disabled={actingId === detailRow.userId}
                onClick={() => {
                  setApproveTarget(detailRow);
                  setDetailRow(null);
                }}
              >
                Approve
              </Button>
            </>
          )}
          {detailRow &&
            isAwaitingPaymentTab &&
            canCancelAdminDecision(detailRow) && (
              <Button
                className={MODAL_BTN_PRIMARY}
                disabled={actingId === detailRow.userId}
                onClick={() => {
                  setCancelTarget(detailRow);
                  setDetailRow(null);
                }}
              >
                Cancel decision
              </Button>
            )}
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!approveTarget}
        onHide={() => setApproveTarget(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Approve application</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            Approve the Young Professional application for{" "}
            <strong>
              {approveTarget?.firstName} {approveTarget?.lastName}
            </strong>
            ?
          </p>
          <p className="small text-muted mb-0">
            {approveTarget &&
            isProceedingAtPremium10WhilePending(approveTarget) ? (
              isActivePaidEarlyStageMember(approveTarget) ? (
                <>
                  This member is already paying £10/month. Approving will
                  schedule the Young Professional £2/month rate from their next
                  Stripe invoice.
                </>
              ) : (
                <>
                  This member chose to continue at £10/month while their ID is
                  reviewed. Approving will make them eligible for the £2/month
                  Young Professional rate.
                </>
              )
            ) : (
              <>
                The member will be eligible for the £2/month Young Professional
                rate. An approval email will be sent if applicable.
              </>
            )}
          </p>
        </Modal.Body>
        <Modal.Footer className="d-flex flex-wrap gap-2 justify-content-end border-0 pt-0">
          <Button
            variant="light"
            className={MODAL_BTN_SECONDARY}
            onClick={() => setApproveTarget(null)}
          >
            Cancel
          </Button>
          <Button
            className={MODAL_BTN_PRIMARY}
            disabled={actingId === approveTarget?.userId}
            onClick={() => void onApproveConfirm()}
          >
            {actingId === approveTarget?.userId ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Approving…
              </>
            ) : (
              "Confirm approval"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!rejectTarget}
        onHide={() => setRejectTarget(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Reject application</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted mb-2">
            {rejectTarget?.firstName} {rejectTarget?.lastName} (
            {rejectTarget?.email})
          </p>
          <Form.Group>
            <Form.Label>Reason (required)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="d-flex flex-wrap gap-2 justify-content-end border-0 pt-0">
          <Button
            variant="light"
            className={MODAL_BTN_SECONDARY}
            onClick={() => setRejectTarget(null)}
          >
            Cancel
          </Button>
          <Button
            variant="outline-danger"
            className={MODAL_BTN_DANGER}
            disabled={actingId === rejectTarget?.userId}
            onClick={() => void onRejectConfirm()}
          >
            Reject
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!cancelTarget}
        onHide={() => setCancelTarget(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Cancel decision</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            Cancel the{" "}
            {cancelTarget?.verificationStatus === "Approved"
              ? "approval"
              : "rejection"}{" "}
            for{" "}
            <strong>
              {cancelTarget?.firstName} {cancelTarget?.lastName}
            </strong>
            ?
          </p>
          <p className="small text-muted mb-0">
            The application will return to Pending Applications as if awaiting
            first review. All approve/reject changes will be undone, including
            Stripe next-invoice updates when applicable.
          </p>
        </Modal.Body>
        <Modal.Footer className="d-flex flex-wrap gap-2 justify-content-end border-0 pt-0">
          <Button
            variant="light"
            className={MODAL_BTN_SECONDARY}
            onClick={() => setCancelTarget(null)}
          >
            Close
          </Button>
          <Button
            className={MODAL_BTN_PRIMARY}
            disabled={actingId === cancelTarget?.userId}
            onClick={() => void onCancelConfirm()}
          >
            {actingId === cancelTarget?.userId ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Cancelling…
              </>
            ) : (
              "Confirm cancel"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EarlyStageVerifications;
